import {Component, computed, input} from '@angular/core';
import {Tooltip} from 'primeng/tooltip';
import {BasicTaDetails} from '../../../api';
import { prepaidIndicator} from '../../../util/general.utils';
import { PrepaidIndicator, isFlightOnlyTa } from '../../../util/app.types';

/**
 * Renders the prepaid state of a TA as a piggy bank icon in the TA list tables:
 * nothing for a TA that was never prepaid, green for a prepaid TA, and red once a prepaid TA has
 * been acquitted for less than its original estimate - or was cancelled after its prepayment had
 * been paid out, where the whole prepayment has to be recovered. The red icon persists into the
 * terminal Cancelled status: the money is still owed back.
 *
 * A Flights Only TA shows a plane instead. It never overlaps the prepaid states (the server
 * forbids prepaying a Flights Only TA), but those still take precedence.
 */
@Component({
  selector: 'prepaid-indicator',
  imports: [
    Tooltip,
  ],
  templateUrl: './prepaid-indicator.component.html',
})
export class PrepaidIndicatorComponent {
  taDetails = input<BasicTaDetails | null | undefined>();
  state = computed<PrepaidIndicator | 'flightOnly'>(() => {
    const prepaid = prepaidIndicator(this.taDetails());
    return prepaid === 'none' && isFlightOnlyTa(this.taDetails()) ? 'flightOnly' : prepaid;
  });

  // Both routes to the red icon mean money is coming back, but for different reasons - a
  // cancellation recovers the whole prepayment rather than the shortfall against an acquittal.
  // The pending case is worded as a warning: the TA is not cancelled yet and still holds its value
  // until the cancellation is approved.
  overpaidTooltip = computed<string>(() => {
    const status = this.taDetails()?.status;
    if (status === 'PendingCancelApproval') {
      return 'Prepaid - cancellation pending approval, prepayment will be recovered';
    }
    if (status === 'CancelApproved' || status === 'Cancelled') {
      return 'Prepaid - TA cancelled, prepayment to be recovered';
    }
    return 'Prepaid - TA has been overpaid';
  });
}
